import { Component, ElementRef, Input, ViewChild } from "@angular/core";
import {
  AlertController,
  ModalController,
  PopoverController,
} from "ionic-angular";
import moment from "moment";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import { PatientBenefitsService } from "providers/patient-charts/patient-benefits.service";
import { ReferralsServiceProvider } from "providers/patient-charts/referrals-service";
import { PayersService } from "providers/payers-service/payers.service";
import { UtilityService } from "providers/utility-service/utility-service";
import { PatientBenefits, PatientEligibilityData } from "../models";

@Component({
  selector: "patient-eligibility",
  templateUrl: "patient-eligibility.html",
})
export class PatientEligibility {
  responseMsg: string = "No benefits eligibility found.";
  @Input() instantEligibility: boolean;
  @Input() currentPage: number = 0;
  @Input() totalPageCount: number = 0;
  patientId: number;
  patientChartId: number;
  eligibilityList: PatientEligibilityData[] = [];
  patientBenefitsList: PatientBenefits[] = [];
  patientBenefitsClonedList: PatientBenefits[] = [];
  patientFirstName: string;
  patientLastName: string;
  patientDateOfBirth: string;
  careType: string;
  activeBenefitList: PatientBenefits[] = [];
  expiredBenefitList: PatientBenefits[] = [];
  primaryBenefitsList: PatientBenefits[] = [];
  secondaryBenefitsList: PatientBenefits[] = [];
  roomAndBoardBenefitsList: PatientBenefits[] = [];
  mostRecentPrimaryBenefit: PatientBenefits = null;
  mostRecentSecondaryBenefit: PatientBenefits = null;

  statusOption: any[] = [];
  statusList: any;
  selectedStatus: any;
  containerWidth: any;

  filteredEligibilityList: PatientEligibilityData[] = [];

  @ViewChild("itemContainer") itemContainer: ElementRef;

  constructor(
    public patientBenefitService: PatientBenefitsService,
    private utilService: UtilityService,
    private navBarService: NavBarServiceProvider,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private payersService: PayersService,
    private referralService: ReferralsServiceProvider,
    private popover: PopoverController
  ) {}

  ngOnInit() {
    this.patientId = +this.navBarService.getPatientChartId();
    this.patientChartId = +this.navBarService.getExternalId();
    this.retrievePatient(this.patientId);
    this.retrievePatientBenefitsList();
    this.getEligibility(this.patientId);
    this.filteredEligibilityList = this.eligibilityList;
    this.initializeStatusOptions();
  }

  initializeStatusOptions() {
    this.statusOption = [
      { value: "Processed", name: "Processed", checked: false },
      { value: "Failed", name: "Failed", checked: false },
    ];
  }

  getEligibility(patientId) {
    this.eligibilityList = [];
    this.utilService.increaseSpinnerCount("Getting Benefits Eligibility...");
    this.patientBenefitService
      .getInstantEligibility(patientId)
      .subscribe((data) => {
        if (data && data.length > 0) {
          data.sort((a, b) => (a.dateRun < b.dateRun ? 1 : -1));
          data.forEach((element) => {
            const patientEligibilityData: PatientEligibilityData =
              {} as any as PatientEligibilityData;
            patientEligibilityData.payerName = element.payerName;
            patientEligibilityData.payerPrimacyLevelTitle = element.payerLevel;
            patientEligibilityData.runDate = moment(element.dateRun).format(
              "MM/DD/YYYY"
            );
            patientEligibilityData.runBy = element.runBy;
            patientEligibilityData.status = element.status;
            patientEligibilityData.response = null;
            patientEligibilityData.benefitType = element.benefitType;
            patientEligibilityData.patientChartId = patientId;
            patientEligibilityData.payerId = element.payerId;
            patientEligibilityData.dateRun = element.dateRun;
            patientEligibilityData.dataType = element.dataType;
            patientEligibilityData.rawOutput = element.responseData.RawOutput;
            this.eligibilityList.push(patientEligibilityData);
          });
          this.filteredEligibilityList = this.eligibilityList;
          this.utilService.decreaseSpinnerCount();
        } else {
          this.filteredEligibilityList = [];
          this.utilService.decreaseSpinnerCount();
        }
        this.appendMediCalResponses(patientId);
      });
    this.utilService.decreaseSpinnerCount();
  }

  /**
   * CO-3399 (A.5.3 / A.5.4): add the stored Medi-Cal 271 responses to the list.
   *
   * A SECOND FETCH rather than a merged one, because these are different runs from a
   * different store - getInstantEligibility reads the claim-submission records, these are
   * written by POST /patient-benefits/medi-cal-eligibility into the patient-benefits table.
   *
   * IT FAILS QUIETLY ON PURPOSE. The Medicare and commercial rows above are the established
   * content of this screen; a Medi-Cal read that errors must not empty it or leave the
   * spinner up. A tenant with no Medi-Cal work simply gets nothing back, which is the same
   * outcome as an error and is fine - the rows are additive either way.
   */
  appendMediCalResponses(patientId) {
    this.patientBenefitService.getMediCalEligibilityResponses(patientId).subscribe(
      (result) => {
        if (!result || !result.recorded || !result.responses) {
          return;
        }
        result.responses.forEach((response) => {
          const row: PatientEligibilityData = {} as any as PatientEligibilityData;
          row.payerName = response.planName || "Medi-Cal";
          row.payerPrimacyLevelTitle = "";
          row.runDate = moment(response.receivedAt).format("MM/DD/YYYY");
          row.runBy = response.requestedByUsername;
          row.status = response.status;
          row.patientChartId = patientId;
          row.dateRun = response.receivedAt;
          row.rawOutput = response.rawResponse;
          // The three Medi-Cal fields the row component renders. Passed through as-is so
          // that "the payer did not say" (undefined) and "the payer said zero" stay
          // distinct all the way to the screen.
          (row as any).aidCode = response.aidCode;
          (row as any).shareOfCostCents = response.shareOfCostCents;
          (row as any).mcpEnrollment = response.mcpEnrollment;
          (row as any).diffs = response.diffs;
          (row as any).diffsComputed = response.diffsComputed;
          this.eligibilityList.push(row);
        });
        this.eligibilityList.sort((a, b) => (a.dateRun < b.dateRun ? 1 : -1));
        this.filteredEligibilityList = this.eligibilityList;
      },
      () => {
        // Deliberately silent - see the comment above.
      }
    );
  }

  refreshData() {
    this.getEligibility(this.patientId);
  }

  callPopup() {
    if (this.getCount() > 0) {
      let runEligibilityModal = this.modalCtrl.create(
        "RunEligibilityModalComponent",
        { benefitList: this.activeBenefitList }
      );
      runEligibilityModal.onDidDismiss((data) => {
        if (data) {
          let response = this.createRequest(data);
          if (Object.keys(response).length > 0) {
            this.utilService.increaseSpinnerCount(
              "Running Instant Eligibility..."
            );
            this.checkReciversId(response, data);
          }
        }
      });

      runEligibilityModal.present();
    } else {
      const errMsg = "There are no active benefits.";
      this.onFailure(errMsg);
    }
  }

  checkReciversId(responseData, data) {
    let finalData = this.activeBenefitList.filter(
      (x) => x.benefitsId == data
    )[0];
    let details = this.createPayerSearchRequestBody(finalData.payerId);
    this.payersService.searchPayersFromOS(details).subscribe(
      (response) => {
        if (
          response !== null &&
          response["hits"] !== null &&
          response["hits"].length > 0
        ) {
          response["hits"].forEach((payerDto) => {
            let instantReceiverId = payerDto.instantReceiverId;
            if (!instantReceiverId) {
              this.onFailure(
                "Payer Missing Eligibility/ReceiverID in Eligibility"
              );
              this.utilService.decreaseSpinnerCount();
            } else {
              this.postInstantEligibility(responseData);
            }
          });
        }
      },
      (err) => {
        this.onFailure("Error: Retrieving Payer's information.");
      }
    );
  }

  postInstantEligibility(response) {
    this.patientBenefitService.postInstantEligibility(response).subscribe(
      (finalData) => {
        if (finalData) {
          const msg =
            "Eligibility request has been sent, it can take a few seconds to a minute for the response to display.";
          this.showConfirmation(msg);
          this.utilService.decreaseSpinnerCount();
        }
      },
      (error) => {
        this.onFailure(error.error);
        this.utilService.decreaseSpinnerCount();
      }
    );
  }

  getCount() {
    let count = 0;
    let activeBenefitList = this.activeBenefitList;
    if (activeBenefitList != null && activeBenefitList.length > 0) {
      count = activeBenefitList.length;
    }
    return count;
  }

  onPageChanged(newPageNum) {
    this.currentPage = newPageNum;
    this.getEligibility(this.patientId);
  }

  retrievePatient(patientId) {
    this.referralService
      .getPatientById(patientId, false)
      .subscribe((response) => {
        if (response != null) {
          this.patientFirstName = response.firstName;
          this.patientLastName = response.lastName;
          this.patientDateOfBirth = this.formatBirthDate(response.birthDate);
        }
      });
    this.careType = String(this.referralService.CARE_TYPE).toUpperCase();
  }

  retrievePatientBenefitsList() {
    this.responseMsg = "";
    this.utilService.increaseSpinnerCount("Loading Benefits...");
    this.patientBenefitService
      .getPatientBenefits(this.patientChartId)
      .subscribe(
        (response) => {
          if (response != null) {
            this.formatPatientBenefitsResponse(response);
          } else {
            this.resetBenefitsList();
            this.responseMsg = "No benefits found.";
          }
          this.utilService.decreaseSpinnerCount();
        },
        (error) => {
          this.resetBenefitsList();
          this.utilService.decreaseSpinnerCount();
          this.responseMsg =
            "error while retrieving benefits, please try again!";
        }
      );
  }

  formatPatientBenefitsResponse(response: PatientBenefits[]) {
    this.resetBenefitsList();

    this.patientBenefitsClonedList = JSON.parse(JSON.stringify(response));

    this.primaryBenefitsList = response.filter(
      (f) => f.payerPrimacyLevel === "PRIMARY"
    );
    if (this.primaryBenefitsList.length > 1) {
      this.primaryBenefitsList.sort(
        (a, b) =>
          new Date(b.effectiveDate).getTime() -
          new Date(a.effectiveDate).getTime()
      );
    }

    if (this.primaryBenefitsList.length > 0) {
      this.mostRecentPrimaryBenefit = JSON.parse(
        JSON.stringify(this.primaryBenefitsList[0])
      );
    }

    this.secondaryBenefitsList = response.filter(
      (f) => f.payerPrimacyLevel === "SECONDARY"
    );
    if (this.secondaryBenefitsList.length > 1) {
      this.secondaryBenefitsList.sort(
        (a, b) =>
          new Date(b.effectiveDate).getTime() -
          new Date(a.effectiveDate).getTime()
      );
    }

    if (this.secondaryBenefitsList.length > 0) {
      this.mostRecentSecondaryBenefit = JSON.parse(
        JSON.stringify(this.secondaryBenefitsList[0])
      );
    }

    this.roomAndBoardBenefitsList = response.filter(
      (f) => f.payerPrimacyLevel === "ROOMANDBOARD"
    );

    this.patientBenefitsList.push(...this.primaryBenefitsList);
    this.patientBenefitsList.push(...this.secondaryBenefitsList);
    this.patientBenefitsList.push(...this.roomAndBoardBenefitsList);

    this.patientBenefitsList.forEach((f) => {
      this.mapPrimacyLevelTitle(f);
      f.effectiveDate = this.formatDate(f.effectiveDate);
      if (f.expiredDate && f.expiredDate !== "-") {
        f.expiredDate = this.formatDate(f.expiredDate);
      }

      for (const key in f) {
        if (typeof f[key] === "object" && f[key] !== null) {
          for (const subKey in f[key]) {
            if (f[key][subKey] === null || f[key][subKey] === "") {
              f[key][subKey] = "-";
            }
          }
        } else {
          if (f[key] === null || f[key] === "") {
            f[key] = "-";
          }
        }
      }
    });

    this.expiredBenefitList = this.patientBenefitsList.filter(
      (f) => f.expiredDate !== "-" && moment(f.expiredDate).isBefore(Date.now())
    );

    this.activeBenefitList = this.patientBenefitsList.filter(
      (f) =>
        moment(f.effectiveDate).isSameOrBefore(moment().format("MM/DD/YYYY")) &&
        (f.expiredDate === "-" ||
          moment(f.expiredDate).isSameOrAfter(moment().format("MM/DD/YYYY")))
    );
  }

  resetBenefitsList() {
    this.patientBenefitsClonedList = [];
    this.patientBenefitsList = [];
    this.primaryBenefitsList = [];
    this.secondaryBenefitsList = [];

    this.mostRecentPrimaryBenefit = null;
    this.mostRecentSecondaryBenefit = null;
  }

  mapPrimacyLevelTitle(patientBenefit: PatientBenefits) {
    if (patientBenefit.payerPrimacyLevel == "PRIMARY") {
      patientBenefit.payerPrimacyLevelTitle = "Primary";
    }
    if (patientBenefit.payerPrimacyLevel == "SECONDARY") {
      patientBenefit.payerPrimacyLevelTitle = "Secondary";
    }
    if (patientBenefit.payerPrimacyLevel == "ROOMANDBOARD") {
      patientBenefit.payerPrimacyLevelTitle = "Room And Board";
    }
  }

  formatDate(date) {
    if (date !== null && date != "") {
      return moment(date).format("MM/DD/YYYY");
    } else {
      return date;
    }
  }

  showConfirmation(msg: string) {
    let alert = this.alertCtrl.create({
      title: "Success",
      message: msg,
      buttons: [
        {
          text: "OK",
        },
      ],
    });
    alert.present();
  }

  onFailure(msg: string) {
    this.utilService.decreaseSpinnerCount();
    const alert = this.alertCtrl.create({
      title: "Error Message",
      message: msg,
      buttons: [{ text: "Cancel" }],
    });
    alert.present();
  }

  createRequest(data) {
    let object = {};
    let finalData = this.activeBenefitList.filter(
      (x) => x.benefitsId == data
    )[0];
    let payerLevel = finalData["payerPrimacyLevel"];
    if (payerLevel != "ROOMANDBOARD") {
      if (this.careType == "PALLIATIVE") {
        payerLevel = "PALLIATIVE";
      } else {
        payerLevel = "HOSPICE";
      }
    }
    if (
      finalData &&
      finalData.subscriber &&
      finalData.subscriber.relationship == "SELF"
    ) {
      object = {
        payerId: parseInt(finalData.payerId),
        benefitType: payerLevel,
        patientFirstName: finalData.subscriber.firstName,
        patientLastName: finalData.subscriber.lastName,
        patientBirthDate: finalData.subscriber.dateOfBirth,
        patientRelationshipToInsured: finalData.subscriber.relationship,
        subscriberId: finalData.subscriber.subscriberId,
        patientChartId: this.patientId,
        payerLevel: finalData.payerPrimacyLevel,
      };
    } else if (finalData && finalData.subscriber) {
      object = {
        payerId: parseInt(finalData.payerId),
        benefitType: payerLevel,
        patientFirstName: this.patientFirstName,
        patientLastName: this.patientLastName,
        patientBirthDate: this.patientDateOfBirth,
        patientRelationshipToInsured: finalData.subscriber.relationship,
        subscriberId: finalData.subscriber.subscriberId,
        patientChartId: this.patientId,
        payerLevel: finalData.payerPrimacyLevel,
      };
    }

    object = this.removeNullFields(object);
    return object;
  }

  removeNullFields(obj: any): any {
    const newObj: any = {};
    for (const key in obj) {
      if (obj[key] !== null && obj[key] !== undefined && obj[key] !== "-") {
        newObj[key] = obj[key];
      }
    }
    return newObj;
  }

  createPayerSearchRequestBody(payerId) {
    let details: any = {};
    details.dataType = "PayerConfig";
    details.mustFields = [
      {
        name: "dataType",
        value: "PayerConfig",
      },
      {
        name: "payerId",
        value: payerId.toString(),
      },
    ];
    details.SearchString = null;
    details.dateRange = null;
    details.OptionalFields = [];
    details.sortBy = {
      name: "payerName",
      value: "asc",
    };
    details.From = 0;
    details.Size = 10;
    return details;
  }

  formatBirthDate(date: any) {
    return date == null ? null : moment(date).format("YYYY-MM-DD");
  }

  selectStatus(event) {
    this.containerWidth = event.target.closest(".item-container").offsetWidth;
    if (this.statusOption.length > 0) {
      const popover = this.popover.create(
        "CareTeamsSelectPage",
        {
          options: this.statusOption,
          searchable: false,
          searchPlaceholder: "Search Status",
          emptyMessage: "No Status found.",
          isMultiSelect: true,
          isAutoWidth: true,
          containerWidth: this.containerWidth,
          isLongOptionName: false,
        },
        { cssClass: "multiple-select-custom-popover" }
      );
      popover.onDidDismiss((data) => {
        if (data) {
          this.statusOption = data.options;
          this.setNewStatus();
        }
      });
      popover.present({ ev: event });
    }
  }

  setNewStatus() {
    const selectedElements = this.statusOption.filter((ele) => ele.checked);
    this.selectedStatus = selectedElements.map((ele) => ele.name);
    this.statusList = this.selectedStatus.join(", ");
  }

  applyEligibilityFilter() {
    if (this.selectedStatus && this.selectedStatus.length > 0) {
      this.filteredEligibilityList = this.eligibilityList.filter((item) =>
        this.selectedStatus.includes(item.status)
      );
    } else {
      this.filteredEligibilityList = this.eligibilityList;
    }
  }

  clearEligibilityFilter() {
    this.statusOption = [
      { value: "Processed", name: "Processed", checked: false },
      { value: "Failed", name: "Failed", checked: false },
    ];
    this.selectedStatus = [];
    this.statusList = "";
    this.filteredEligibilityList = this.eligibilityList;
  }
}



// WEBPACK FOOTER //
// ./src/pages/patient-chart/patient-benefits/patient-eligibility/patient-eligibility.ts